import {
    AttributeType,
    AttributeValue,
    PropsAttribute,
} from '../components/ui-element.type.js';
import { resolvePropertyDescriptor } from '../components/ui-element.helper.js';
import { nameConverter } from '../global/render-api.js';
import type { UIElement } from '../components/ui-element.js';
import type { GenericDecorator } from './decorator.type.js';
import {
    DefaultAttributeReflect,
    DefaultAttributeType,
} from '../components/ui-element.const.js';
import { isPlainObject } from '../global/helpers.js';

type AttributeDecoratorParam = PropsAttribute | AttributeType;

const isPropsAttribute = (
    params?: AttributeDecoratorParam
): params is PropsAttribute => isPlainObject(params);

const getInitialValueType = (value: AttributeValue): AttributeType =>
    typeof value as AttributeType;

export function Attribute(
    params: PropsAttribute
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute(
    type: AttributeType
): GenericDecorator<void, ClassFieldDecoratorContext>;
export function Attribute(): GenericDecorator<void, ClassFieldDecoratorContext>;

/**
 * Defines an attribute for the component.
 * @param {PropsAttribute | AttributeType} typeOrParams
 */
export function Attribute(
    typeOrParams?: AttributeDecoratorParam
): GenericDecorator<void, ClassFieldDecoratorContext> {
    return (_: void, context: ClassFieldDecoratorContext) => {
        if (context.kind === 'field') {
            context.addInitializer(function (this: unknown) {
                const instance = this as InstanceType<typeof UIElement>;
                const propertyName = String(context.name);
                const existingDescriptor = Object.getOwnPropertyDescriptor(
                    instance,
                    propertyName
                );
                const initialValue: AttributeValue =
                    existingDescriptor?.value as AttributeValue;
                let reflect: boolean = DefaultAttributeReflect;
                let adjustedType: AttributeType;
                if (isPropsAttribute(typeOrParams)) {
                    adjustedType = typeOrParams.type ?? DefaultAttributeType;
                    reflect = typeOrParams?.reflect !== false;
                } else {
                    adjustedType =
                        typeOrParams ??
                        getInitialValueType(initialValue) ??
                        DefaultAttributeType;
                }
                const attributeName =
                    nameConverter.propToAttribute(propertyName);
                const descriptor = resolvePropertyDescriptor(
                    attributeName,
                    adjustedType,
                    initialValue,
                    reflect
                );
                Object.defineProperty(instance, propertyName, descriptor);
            });
        } else {
            throw new Error(
                '@Attribute decorator is only applied to the property'
            );
        }
    };
}
